import {defineStore} from "pinia";
import {ref, computed} from "vue";
import {IUser} from "@/models/IUser";
import {useI18n} from 'vue-i18n';
import dayjs from 'dayjs';
import {useTripsStore} from "./tripsStore";
import {getCurrencies} from "@/services/core.service";

export const useUserStore = defineStore("user", () => {
    const user = ref<IUser>(null);
    const loading = ref(false);
    

    const currencies = ref([
        { symbol: "€", label: "Euro", value: "euro", code: "EUR", rate: 1.0 },
        { symbol: "£", label: "British Pound", value: "pound", code: "GBP", rate: 1.6 },
        { symbol: "$", label: "US Dollar", value: "dollar", code: "USD", rate: 1.6 }
    ]);


    const i18n = useI18n({useScope: 'global'});
    const tripsStore = useTripsStore();
    const currency = ref({
            'symbol': '€',
            'label': '€',
            'value': 'euro',
            'code': 'EUR',
            'rate': 1.0000000
        })
    const preferences = ref({
        dateFormat: 'standard',
        measurementSystem: 'metric' // Default to metric system
    });


    const isLoggedIn = computed(() => user.value !== null);

    // const currentLocale = computed(() => preferences.value.locale);
    const currentDateFormat = computed(() => preferences.value.dateFormat);
    const currentMeasurementSystem = computed(() => preferences.value.measurementSystem);


    function loadPreferences() {
        loading.value = true;
        getCurrencies().then((response) => {
            response.data._embedded?.currencies.forEach((item) => {
                const c =currencies.value.find((cur) => cur.code == item.code)

                if(c){
                    c.rate = item.rate
                }
            });

            // const savedLocale = localStorage.getItem('locale');
            const savedCurrency = localStorage.getItem('currency');
            const savedMeasurementSystem = localStorage.getItem('measurementSystem');

            let selectedCurrency = currencies.value.find((c)=>c.value == savedCurrency)
            if(!selectedCurrency){
                selectedCurrency = currencies.value[0]
            }

            // if (savedLocale) updateLocale(savedLocale);
            if (savedCurrency) setCurrency(selectedCurrency);
            if (savedMeasurementSystem) setMetricsFormat(savedMeasurementSystem);
        }).finally(() => {
            loading.value = false;
        })
    }

    function login(newUser: IUser) {
        user.value = newUser;
        loadPreferences();
    }

    function logout() {
        user.value = null;
        tripsStore.trips = [];
    }
    
    
    function setMetricsFormat(newMeasurementSystem: "metric" | "imperial") {
        preferences.value.measurementSystem = newMeasurementSystem;
        localStorage.setItem('measurementSystem', newMeasurementSystem);
        // TODO: Send updated user preferences to server
        // Example: api.updateUserPreferences({ measurementSystem: newMeasurementSystem });
    }


    function formatDateWithMapping(date, mapping) {
        const formatMapping = {
            numeric: 'YYYY-MM-DD',
            standard: 'DD/MM/YYYY',
            shortText: 'DD MMM YYYY',
            longText: 'DD MMMM, YYYY',
            withWeekday: 'ddd, DD MMMM, YYYY'
        };
        const format = formatMapping[mapping] || formatMapping.standard;
        return dayjs(date).format(format);
    }

    function formatDateWithCustomPatternMapping(date, pattern) {
        return dayjs(date).format(pattern) || dayjs(date).format("YYYY-MM-DD");
    }


    function formatDate(date) {
        const formatMapping = {
            numeric: 'YYYY-MM-DD',
            standard: 'DD/MM/YYYY',
            shortText: 'DD MMM YYYY',
            longText: 'DD MMMM, YYYY',
            withWeekday: 'ddd, DD MMMM, YYYY'
        };

        const format = formatMapping[currentDateFormat.value] || formatMapping.standard;
        return dayjs(date).format(format);
    }



    function setCurrency(newCurrency) {
        currency.value = newCurrency;
        localStorage.setItem('currency', newCurrency.value);
    }

    function inCurrency(amount, code=null) {

        let cur = currency.value;

        if(!code){
            const findCurrency =  currencies.value.find((c)=>c.code == code)
            if(findCurrency){
               cur = findCurrency;
            }
        }

        // Use 'de-DE' formatting, regardless of the current locale( We use en-US so we override it here)
        const formattedAmount = new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: cur.code,
        }).format(amount);

        // Reorder the symbol to the front
        const currencySymbol = cur.symbol || '';
        const numericPart = formattedAmount.replace(currencySymbol, '').trim();
        return `${currencySymbol}${numericPart}`;
    }

    function round(amount) {
        if (!amount) {
            return 0.0;
        }

        let exchangedRate = Number(amount) * currency.value.rate;

        return Math.floor(exchangedRate/ 10) * 10;
    }

    function formatCurrency(amount, round=true) {
        if (!amount) {
            return '';
        }

        let exchangedRate = Number(amount) * currency.value.rate;

        if(round){
            exchangedRate = Math.floor(exchangedRate/ 10) * 10;
        }

        return inCurrency(exchangedRate)
    }

    function fromCurrency(amount, code) {
        if (!amount) {
            return '';
        }
        const fromCurrency =  currencies.value.find((c)=>c.code == code)
        if(!fromCurrency){
            return '';
        }

        let exchangedRate = Number(amount) / fromCurrency.rate;
        return exchangedRate;
    }




    function formatLength(value) {
        if (preferences.value.measurementSystem === 'metric') {
            return `${(value).toFixed(0)} m (${(value * 3.28084).toFixed(2)} ft)`;  // or meters, based on value
        } else {
            return `${(value * 3.28084).toFixed(2)} ft`;  // Convert cm to inches
        }
    }

    function formatWeight(value) {
        if (preferences.value.measurementSystem === 'metric') {
            return `${value} kg`;  // or grams, based on value
        } else {
            return `${(value * 2.20462).toFixed(2)} lbs`;  // Convert kg to lbs
        }
    }


    function isLocaleSelected(value) {
        return i18n.locale.value == value
    }

    function isCurrencySelected(c) {
        return currency.value.value == c
    }


    // Load preferences initially
    loadPreferences();

    return {
        user,
        currency,
        currencies,
        isLoggedIn,
        currentDateFormat,
        currentMeasurementSystem,
        preferences,
        login,
        logout,
        setCurrency,
        setMetricsFormat,
        formatDate,
        formatCurrency,
        fromCurrency,
        inCurrency,
        round,
        formatDateWithMapping,
        formatDateWithCustomPatternMapping,
        formatLength,
        formatWeight,
        isLocaleSelected,
        isCurrencySelected
    };
});
